// utils/prepaidCreateIdempotency.ts
//
// Explicit attempt-lifecycle state machine + canonical payload/fingerprint
// helpers for POST /prepaids (Prepaid creation), used by
// pages/prepaids/NewPrepaid.tsx -- the only frontend caller of this
// endpoint. Modeled directly on utils/depositCreateIdempotency.ts's
// "idle" | "in-flight" | "ambiguous" design.
//
// Like NewDeposit.tsx, NewPrepaid.tsx remounts fresh every time the user
// navigates to /prepaids/new -- so this module tracks exactly ONE
// PrepaidCreateAttempt per mounted page instance, with no resource-id
// scoping and no "cross-resource" blocked state (contrast with
// prepaidExpenseIdempotency.ts, whose PrepaidDetail.tsx host is mounted
// once for the route and reused across different prepaid_id values).

export type PrepaidCreatePayload = {
  vendor_id: number | null;
  bank_account_id: number;
  amount: string;
  date: string;
  memo: string | null;
};

/**
 * Deterministic canonical payload for POST /prepaids.
 * Callers must pass the SAME object (with idempotency_key added) as the
 * actual request body, never a separately reconstructed copy, so the
 * fingerprint can never drift from what is truly sent.
 *
 * - vendor_id: nullable (Prepaid.vendor_id is optional), canonicalized to a
 *   number when present so "12" and 12 never produce different
 *   fingerprints, and to null for any falsy input -- matching the
 *   backend's own `int(vendor_id) if vendor_id else None`
 *   (backend/prepaids_router.py's _prepaid_create_canonical_payload).
 * - bank_account_id: canonicalized to a number.
 * - amount: fixed 2-decimal string. This app has no frontend-visible
 *   company money-scale setting anywhere (confirmed by repository search,
 *   mirroring depositCreateIdempotency.ts's identical finding) -- every
 *   existing money input in this app assumes 2 decimals client-side,
 *   matching the backend's own q2 (fixed CENT = Decimal("0.01")) used
 *   throughout prepaids_router.py. This mirrors canonicalDepositCreate's
 *   identical `.toFixed(2)` choice.
 * - date: the exact submitted YYYY-MM-DD.
 * - memo: trimmed, empty string canonicalized to null -- matching the
 *   backend's own `(payload.memo or "").strip() or None`.
 */
export function canonicalPrepaidCreate(
  vendorId: string | number | null | undefined,
  bankAccountId: string | number,
  amount: string | number,
  date: string,
  memo: string,
): PrepaidCreatePayload {
  return {
    vendor_id: vendorId ? Number(vendorId) : null,
    bank_account_id: Number(bankAccountId),
    amount: Number(amount).toFixed(2),
    date,
    memo: memo.trim() || null,
  };
}

export function fingerprintPrepaidCreate(payload: PrepaidCreatePayload): string {
  return JSON.stringify(payload);
}

export function randomUuidKey(): string {
  return crypto.randomUUID();
}

// ---------------------------------------------------------------------------
// Attempt lifecycle
// ---------------------------------------------------------------------------

/**
 * - "idle": no active attempt. The next submission always mints a fresh
 *   key.
 * - "in-flight": a request has been sent and its outcome is not yet
 *   known. Transitions to "idle" (success or definite rejection) or
 *   "ambiguous" once the response (or its absence) is classified.
 * - "ambiguous": the last attempt's outcome could not be determined
 *   (network failure, timeout, unreadable response, HTTP 500, or any
 *   other unexpected server error). The key/fingerprint/payload are
 *   retained so an unchanged retry safely replays instead of risking a
 *   duplicate Prepaid; an edited retry stays blocked until this attempt is
 *   resolved.
 */
export type PrepaidCreateAttemptState = "idle" | "in-flight" | "ambiguous";

export type PrepaidCreateAttempt = {
  state: PrepaidCreateAttemptState;
  idempotencyKey: string | null;
  submittedFingerprint: string | null;
  submittedPayload: PrepaidCreatePayload | null;
};

export const IDLE_PREPAID_CREATE_ATTEMPT: PrepaidCreateAttempt = {
  state: "idle",
  idempotencyKey: null,
  submittedFingerprint: null,
  submittedPayload: null,
};

export type PrepaidCreateSubmitDecision =
  | { kind: "start"; idempotencyKey: string; attempt: PrepaidCreateAttempt }
  | { kind: "blocked-mismatch" }
  | { kind: "blocked-in-flight" };

/**
 * Pure decision for whether a submit attempt may proceed to the network,
 * given the CURRENT attempt state and the canonical payload/fingerprint
 * just built from the form. Never mutates `attempt` -- returns a NEW
 * attempt (state "in-flight") for the caller to store when
 * `kind === "start"`.
 *
 *  - idle -> always starts a fresh attempt with a newly generated key.
 *  - ambiguous, SAME fingerprint -> unchanged retry: reuses the stored key
 *    exactly (never mints a new one for an ambiguous outcome -- see module
 *    docstring).
 *  - ambiguous, DIFFERENT fingerprint -> blocked-mismatch: the prior
 *    attempt under this key may already have committed; an edited
 *    resubmission under the same key must never be sent.
 *  - in-flight -> blocked-in-flight: defensive. The component's own
 *    synchronous in-flight ref already prevents this from being reached
 *    in practice, but the decision stays correct even if called before
 *    that guard, or from a test, without it.
 */
export function decidePrepaidCreateAttempt(
  attempt: PrepaidCreateAttempt,
  payload: PrepaidCreatePayload,
  fingerprint: string,
  generateKey: () => string,
): PrepaidCreateSubmitDecision {
  if (attempt.state === "in-flight") {
    return { kind: "blocked-in-flight" };
  }

  if (attempt.state === "ambiguous") {
    if (attempt.submittedFingerprint !== fingerprint) {
      return { kind: "blocked-mismatch" };
    }
    return {
      kind: "start",
      idempotencyKey: attempt.idempotencyKey as string,
      attempt: { ...attempt, state: "in-flight" },
    };
  }

  const idempotencyKey = generateKey();
  return {
    kind: "start",
    idempotencyKey,
    attempt: {
      state: "in-flight",
      idempotencyKey,
      submittedFingerprint: fingerprint,
      submittedPayload: payload,
    },
  };
}

/** Confirmed 2xx success (create or successful replay -- both return the
 * same response shape, so no distinction is needed here). Clears the
 * attempt back to idle. */
export function resolvePrepaidCreateSuccess(): PrepaidCreateAttempt {
  return IDLE_PREPAID_CREATE_ATTEMPT;
}

/**
 * Definite pre-commit rejection: any deterministic 4xx this endpoint's
 * stack can produce -- 400 raised by this handler itself (amount <= 0,
 * invalid vendor/bank account, etc.), 401/403 raised by its auth
 * dependencies before the handler body ever runs, 422 from FastAPI's own
 * request-validation short-circuit, a closed-period 409, or an
 * idempotency-conflict 409. In every one of these cases nothing was
 * persisted under this key, so the identity is free to reuse. Returns to
 * idle so the next submission mints a fresh key and is treated as a new
 * logical attempt, with the user free to edit any field.
 */
export function resolvePrepaidCreateDefiniteRejection(): PrepaidCreateAttempt {
  return IDLE_PREPAID_CREATE_ATTEMPT;
}

/**
 * Ambiguous outcome: network failure, timeout, a lost/malformed/unreadable
 * response, HTTP 500, or any other unexpected server error where the
 * operation may have committed before the response was constructed (or
 * before replay validation ran). Retains the key, fingerprint, and
 * submitted payload so an unchanged retry safely replays instead of
 * risking a duplicate Prepaid; an edited retry stays blocked.
 */
export function resolvePrepaidCreateAmbiguousFailure(
  attempt: PrepaidCreateAttempt,
): PrepaidCreateAttempt {
  return { ...attempt, state: "ambiguous" };
}

/**
 * Whether reopening/restoring the New Prepaid form should repopulate the
 * previously submitted form values (vendor/bank account/amount/date/memo)
 * instead of resetting to fresh defaults. True only when there is a live
 * AMBIGUOUS attempt.
 *
 * Restoring matters: resetting to fresh defaults (e.g. today's date)
 * would almost never coincidentally match what was actually submitted, so
 * the very next submission would compute a different fingerprint and get
 * blocked as an "edited retry" -- trapping the user with no visible way to
 * make an unchanged retry that safely reuses the key.
 */
export function shouldRestoreAmbiguousAttempt(attempt: PrepaidCreateAttempt): boolean {
  return attempt.state === "ambiguous";
}

// ---------------------------------------------------------------------------
// Failure classification
// ---------------------------------------------------------------------------

export type PrepaidCreateFailureClass =
  | "definite-rejection"
  | "closed-period"
  | "idempotency-conflict"
  | "ambiguous";

/**
 * Classifies a thrown apiPost error into one of the four outcomes this
 * endpoint can produce. Deliberately does NOT classify by HTTP status
 * alone for 409: both a closed-period rejection and an idempotency-
 * conflict rejection share HTTP 409 (see
 * backend/utils/accounting_guards.py's _assert_not_closed, and
 * backend/prepaids_router.py's IdempotencyRequestConflict handling) but
 * require different messaging and a different interpretation -- a
 * closed-period 409 is detected via `isClosedPeriod`'s message match; any
 * OTHER 409 is treated as an idempotency conflict, mirroring
 * classifyDepositCreateFailure's identical reasoning.
 *
 * Every OTHER 4xx status this endpoint's stack can actually produce --
 * 400 raised directly by this handler, 401/403 raised by auth
 * dependencies (resolve before the handler body runs at all), and 422
 * from FastAPI's own RequestValidationError handler (also short-circuits
 * before the handler runs) -- is treated as a definite rejection
 * uniformly: every one of these occurs strictly before (or in place of)
 * any business write, so nothing was ever persisted under the key.
 *
 * `status` (added to apiPost's thrown error by api/client.ts) is what
 * distinguishes a genuine HTTP response (4xx/5xx) from a network-level
 * failure: fetch() itself rejecting (network down, DNS, CORS, or a
 * connection-level timeout) throws a plain Error/TypeError with no
 * `.response` at all, so `err?.response?.status` is `undefined` for
 * those -- correctly folded into "ambiguous" here, the same as a
 * malformed/unreadable response.
 */
export function classifyPrepaidCreateFailure(
  err: any,
  isClosedPeriod: (err: any) => boolean,
): PrepaidCreateFailureClass {
  const status = err?.response?.status;

  if (typeof status !== "number") {
    // Network failure, timeout, or any other rejection with no HTTP
    // response at all -- outcome unknown, must be treated as ambiguous.
    return "ambiguous";
  }

  if (status === 409) {
    return isClosedPeriod(err) ? "closed-period" : "idempotency-conflict";
  }

  if (status >= 400 && status < 500) {
    // Every other 4xx (400, 401, 403, 422, and any other well-formed 4xx)
    // -- see the module-level rationale above. Deterministic: nothing was
    // ever persisted under this key.
    return "definite-rejection";
  }

  // 5xx (500 and any other), and anything outside the 4xx/409 ranges this
  // app's own code never emits: ambiguous -- the backend's replay loader
  // (_load_prepaid_create) runs AFTER db.commit(), so a 500 can mean the
  // Prepaid WAS persisted and only response construction or replay
  // validation failed. Fails closed for anything not positively identified
  // as a definite pre-commit rejection.
  return "ambiguous";
}

// ---------------------------------------------------------------------------
// User-facing messages
// ---------------------------------------------------------------------------

/**
 * Shown when an edited retry is blocked while an ambiguous attempt
 * remains unresolved. Deliberately does NOT say reloading alone is safe --
 * a reload proves nothing about whether the prior attempt already posted.
 * Must convey all three ideas: (1) the prior attempt may already have been
 * recorded, (2) restoring the original values permits a safe retry under
 * the same key, (3) the user should check the prepaids list before
 * starting a new one.
 */
export const PREPAID_CREATE_EDITED_RETRY_BLOCKED_MESSAGE =
  "This prepaid may already be recorded. Original details restored. Retry, or check the prepaids list first.";

/**
 * Shown for an idempotency-conflict 409: the current key is permanently
 * bound to a different (already-completed) request and can never succeed
 * for this payload. Deliberately does NOT suggest the prepaid may
 * ambiguously have been recorded -- that is not the situation; THIS
 * attempt's data was never persisted under this key.
 */
export const PREPAID_CREATE_CONFLICT_MESSAGE =
  "Couldn't confirm this prepaid. Check the prepaids list before trying again.";
